import { useCallback, useState } from "react";
import { MAX_POS_NAME_LENGTH, ResumeFieldsType } from "../types";
import { defaultResumenFields } from "../constants";
import useAppDispatch from "../../../hooks/useAppDispatch";
import {
  AddMaterialsToProposal,
  AddProductsToProposal,
  SetCurrentStep,
  UpdateProposalInfo,
  UpdateProposalInfoField,
} from "..";
import { ProposalCreationPanes } from "../../../../pages/ProposalCreation/types/panes.types";
import ROUTES from "../../../../router/constants/routes";
import { useIonRouter } from "@ionic/react";
import {
  PackagedBeerDetails,
  PackagedBeerDetailsContainer,
  useCreateProposalMutation,
  useGetLastAcceptedSherpaConditionsLazyQuery,
  useGetPackagedProductsLazyQuery,
  useGetPointOfSalesLazyQuery,
  useGetProposalThemeLazyQuery,
} from "../../../../gql/hooks";
import { AddProductsToProposalType } from "../reducers/productsReducer";
import evaluateCPVConditions from "../utils/evaluateCPVConditions";
import pickProposalTheme from "../utils/pickProposalTheme";

const toString = (
  value: number | null | undefined,
  def: string | undefined = undefined,
) => (value ? value + "" : def);

const useCPVProposal = (posId: number | undefined) => {
  const [loading, setLoading] = useState(false);

  const { push } = useIonRouter();
  const dispatch = useAppDispatch();

  const [fetchPosInfo] = useGetPointOfSalesLazyQuery();
  const [fetchProposalTheme] = useGetProposalThemeLazyQuery();
  const [createProposalMutation] = useCreateProposalMutation();
  const [fetchPackagedProducts] = useGetPackagedProductsLazyQuery();
  const [fetchPosCPVs] = useGetLastAcceptedSherpaConditionsLazyQuery();

  const createProposalWithCPVs = useCallback(async () => {
    if (!posId) return;
    setLoading(true);

    // Add point of Sale
    const posData = await fetchPosInfo({
      variables: {
        id: posId,
      },
    });
    const pos = posData.data?.pointOfSales?.[0];

    if (pos?.id) {
      dispatch(
        UpdateProposalInfoField({
          key: "posId",
          value: pos.id,
        }),
      );
      dispatch(
        UpdateProposalInfoField({
          key: "pos",
          value: pos,
        }),
      );
    }

    // Add Title and expired date if missing
    const name = `${pos?.name.slice(0, MAX_POS_NAME_LENGTH - 11)}_${new Date()
      .toISOString()
      .slice(0, 10)}`;
    dispatch(
      UpdateProposalInfoField({
        key: "name",
        value: name,
      }),
    );

    dispatch(
      UpdateProposalInfoField({
        key: "expireDay",
        value: Date.now() / 1000,
      }),
    );

    // Proposal Resume and Guarantees set to default
    let resumeFields: ResumeFieldsType = defaultResumenFields;
    let guarantees = undefined;

    dispatch(
      UpdateProposalInfo({
        key: "resumeFields",
        value: resumeFields,
      }),
    );
    dispatch(
      UpdateProposalInfo({
        key: "guarantees",
        value: guarantees,
      }),
    );

    // Get all products
    const cpvsData = await fetchPosCPVs({ variables: { posId } });
    const cpvsProducts = cpvsData.data?.lastAcceptedSherpaConditions;

    if (cpvsProducts?.length) {
      const packagedProductExternalIds = cpvsProducts.map(
        (_) => _.packagedProductExternalId,
      );

      const productResponse = await fetchPackagedProducts({
        variables: {
          where: {
            externalId: {
              in: packagedProductExternalIds,
            },
          },
        },
      });

      const kegBrands: number[] = [];
      const bottleBrands: number[] = [];
      const productsInfo = productResponse?.data?.packagedProducts || [];
      const products: AddProductsToProposalType[] = productsInfo.reduce(
        (acc, productInfo, index) => {
          if (productInfo.externalId) {
            const cpvs = cpvsProducts.filter(
              (_) => _.packagedProductExternalId === productInfo.externalId,
            );

            if (cpvs?.length) {
              const productCC = evaluateCPVConditions(
                cpvs,
                productInfo.externalId,
              );

              // Container
              const container = (
                productInfo.packagedDetails as PackagedBeerDetails
              ).container;

              // Set Keg Brands in Proposal
              if (
                (productInfo.packagedDetails as PackagedBeerDetails)
                  .container === PackagedBeerDetailsContainer.Keg &&
                !!productInfo.product.brandId
              ) {
                kegBrands.push(productInfo.product.brandId);
              }

              // Set Bottle Brands in Proposal in case no kegs are put
              if (
                (productInfo.packagedDetails as PackagedBeerDetails)
                  .container === PackagedBeerDetailsContainer.Bottle &&
                !!productInfo.product.brandId
              ) {
                bottleBrands.push(productInfo.product.brandId);
              }

              acc.push({
                order: index,
                productId: productInfo.productId,
                container,
                packagedProductId: productInfo.id,
                packagedProductUnitNb: toString(
                  productCC.packagedProductUnitsNb,
                  "0",
                ),
                freePackagedProductUnitsNb: toString(
                  productCC.freePackagedProductUnitsNb,
                  "0",
                ),
                discountValue: toString(productCC.discountValue),
                discountType: productCC.discountType || undefined,
                discountX: toString(productCC.x),
                discountY: toString(productCC.y),
                recallValue: toString(productCC.recallValue),
                recallType: productCC.recallType || undefined,
                recallX: toString(productCC.recallX),
                recallY: toString(productCC.recallY),
                amortizationValue: toString(productCC.amortizationValue),
                amortizationType: productCC.amortizationType || undefined,
                amortizationX: toString(productCC.amortizationX),
                amortizationY: toString(productCC.amortizationY),
                price: productCC.packagedProductPrice ?? undefined,
                isTank: container === PackagedBeerDetailsContainer.Tank,
              });
            }
          }

          return acc;
        },
        [] as AddProductsToProposalType[],
      );

      // Set Portada
      const { data: proposalThemeData } = await fetchProposalTheme();
      const themeId = pickProposalTheme(
        kegBrands,
        bottleBrands,
        proposalThemeData?.proposalThemes,
      );

      dispatch(
        UpdateProposalInfoField({
          key: "brandId",
          value: themeId || 0,
        }),
      );

      // Set Products
      dispatch(AddProductsToProposal({ products, reset: true }));
      dispatch(AddMaterialsToProposal({ materials: [], reset: true }));

      // Generate ProposalId
      const response = await createProposalMutation({
        variables: {
          expireAt: Date.now(),
          title: name || "",
          pointOfSaleId: posId,
          provinceNumber: undefined,
          isShortProposal: false,
        },
      });

      if (!!response.data?.createProposal) {
        dispatch(
          UpdateProposalInfo({
            key: "proposalId",
            value: response.data?.createProposal.id,
          }),
        );
      }
    }
    dispatch(SetCurrentStep(1));
    setLoading(false);

    push(`${ROUTES.CREATE_PROPOSAL}/${ProposalCreationPanes.PRODUCTS}`);
  }, [
    posId,
    push,
    dispatch,
    fetchPosCPVs,
    fetchPosInfo,
    fetchProposalTheme,
    fetchPackagedProducts,
    createProposalMutation,
  ]);

  return {
    loading,
    createProposalWithCPVs,
  };
};

export default useCPVProposal;
